import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { BoardCommentThread, CommentMention, CRMUser } from '../../../types';
import { useOptionalCRM } from '../../../utils/CRMContext';
import { isInternalCommentMentionUser } from '../../../utils/mentionAffiliation';
import {
  subscribeBoardComments,
  postBoardComment,
  boardCommentCount,
} from '../../../utils/boardCommentService';

export interface BoardCommentAuthor {
  uid: string;
  displayName: string;
  canWrite: boolean;
  kind: 'staff' | 'client';
}

interface BoardCommentsContextValue {
  dealId: string;
  threadsByNodeId: Map<string, BoardCommentThread>;
  expandedNodeId: string | null;
  highlightedNodeId: string | null;
  openComments: (nodeId: string, opts?: { highlight?: boolean }) => void;
  closeComments: () => void;
  postComment: (
    nodeId: string,
    content: string,
    mentions?: CommentMention[],
    contentHtml?: string,
  ) => Promise<void>;
  commentCount: (nodeId: string) => number;
  author: BoardCommentAuthor;
  posting: boolean;
  mentionableUsers: CRMUser[];
}

const BoardCommentsContext = createContext<BoardCommentsContextValue | null>(null);

export function useBoardComments(): BoardCommentsContextValue {
  const ctx = useContext(BoardCommentsContext);
  if (!ctx) {
    throw new Error('useBoardComments must be used within BoardCommentsProvider');
  }
  return ctx;
}

interface BoardCommentsProviderProps {
  dealId: string;
  author: BoardCommentAuthor;
  children: React.ReactNode;
}

export const BoardCommentsProvider: React.FC<BoardCommentsProviderProps> = ({
  dealId,
  author,
  children,
}) => {
  const crm = useOptionalCRM();
  const crmUsers = crm?.crmUsers ?? [];
  const [threads, setThreads] = useState<BoardCommentThread[]>([]);
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const mentionableUsers = useMemo(
    () => crmUsers.filter((u) => isInternalCommentMentionUser(u)),
    [crmUsers],
  );

  useEffect(() => {
    return subscribeBoardComments(dealId, setThreads);
  }, [dealId]);

  const threadsByNodeId = useMemo(() => {
    const map = new Map<string, BoardCommentThread>();
    threads.forEach((t) => map.set(t.nodeId, t));
    return map;
  }, [threads]);

  // Click-away dismiss. `onPaneClick` alone misses clicks on nodes (e.g. inside
  // a group frame), so close on any pointer-down outside the bubble and pins.
  // Capture phase, because pins/bubbles stop propagation on mousedown.
  useEffect(() => {
    if (!expandedNodeId) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (target?.closest('.board-comment-bubble, .board-comment-pin, .rce-mention-popover')) return;
      setExpandedNodeId(null);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [expandedNodeId]);

  const openComments = useCallback((nodeId: string, opts?: { highlight?: boolean }) => {
    setExpandedNodeId(nodeId);
    if (opts?.highlight) setHighlightedNodeId(nodeId);
  }, []);

  const closeComments = useCallback(() => {
    setExpandedNodeId(null);
  }, []);

  const postComment = useCallback(
    async (nodeId: string, content: string, mentions?: CommentMention[], contentHtml?: string) => {
      const trimmed = content.trim();
      if (!trimmed || !author.canWrite) return;
      setPosting(true);
      try {
        await postBoardComment(dealId, nodeId, {
          content: trimmed,
          authorUid: author.uid,
          authorName: author.displayName,
          authorKind: author.kind,
          mentions: mentions ?? [],
          contentHtml,
        });
      } finally {
        setPosting(false);
      }
    },
    [dealId, author],
  );

  const commentCount = useCallback(
    (nodeId: string) => boardCommentCount(threadsByNodeId.get(nodeId)),
    [threadsByNodeId],
  );

  const value = useMemo(
    () => ({
      dealId,
      threadsByNodeId,
      expandedNodeId,
      highlightedNodeId,
      openComments,
      closeComments,
      postComment,
      commentCount,
      author,
      posting,
      mentionableUsers,
    }),
    [
      dealId,
      threadsByNodeId,
      expandedNodeId,
      highlightedNodeId,
      openComments,
      closeComments,
      postComment,
      commentCount,
      author,
      posting,
      mentionableUsers,
    ],
  );

  return (
    <BoardCommentsContext.Provider value={value}>{children}</BoardCommentsContext.Provider>
  );
};
